/**
 * Validation Utilities
 * Comprehensive validation and security functions
 */

import DOMPurify from 'dompurify';

/**
 * Validates the contact form data.
 * @param {Object} formData - The form data object.
 * @returns {Object} - Object with isValid boolean and errors object
 */
export const validateContactForm = (formData) => {
    const errors = {};

    // Name validation
    if (!formData.name?.trim()) {
        errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
        errors.name = "Name must be at least 2 characters";
    } else if (formData.name.trim().length > 100) {
        errors.name = "Name must be less than 100 characters";
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.name.trim())) {
        errors.name = "Name contains invalid characters";
    }

    // Email validation
    if (!formData.email?.trim()) {
        errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        errors.email = "Please enter a valid email address";
    } else if (formData.email.length > 254) {
        errors.email = "Email is too long";
    }

    // Subject validation (optional field)
    if (formData.subject && formData.subject.length > 200) {
        errors.subject = "Subject must be less than 200 characters";
    }

    // Message validation
    if (!formData.message?.trim()) {
        errors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
        errors.message = "Message must be at least 10 characters";
    } else if (formData.message.trim().length > 5000) {
        errors.message = "Message must be less than 5000 characters";
    }

    // Phone validation (optional)
    if (formData.phone && !/^[+]?[\d\s-()]{10,20}$/.test(formData.phone)) {
        errors.phone = "Please enter a valid phone number";
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

/**
 * Validates environment variables on startup.
 * @returns {Object} - Object with isValid boolean and missing variables
 */
export const validateEnv = () => {
    const requiredEnv = []; // No critical client-side env vars required anymore

    const optionalEnv = [
        'REACT_APP_EMAILJS_TEMPLATE_ID_CONFIRMATION',
        'REACT_APP_WHATSAPP_PHONE',
        'REACT_APP_GOOGLE_ANALYTICS_ID'
    ];

    const missing = requiredEnv.filter(key => !process.env[key]);
    const missingOptional = optionalEnv.filter(key => !process.env[key]);

    if (missing.length > 0) {
        console.error(`[CRITICAL] Missing Required Environment Variables: ${missing.join(', ')}`);
        console.warn('Contact form functionality may not work correctly.');
    }

    if (missingOptional.length > 0 && process.env.NODE_ENV === 'development') {
        console.info(`[INFO] Missing Optional Environment Variables: ${missingOptional.join(', ')}`);
    }

    return {
        isValid: missing.length === 0,
        missing,
        missingOptional
    };
};

/**
 * Sanitizes input string using DOMPurify for XSS protection
 * @param {string} input - Input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeInput = (input) => {
    if (!input) return '';

    // Use DOMPurify for comprehensive XSS protection
    if (typeof DOMPurify !== 'undefined') {
        return DOMPurify.sanitize(input, {
            ALLOWED_TAGS: [], // No HTML tags allowed
            ALLOWED_ATTR: []  // No attributes allowed
        });
    }

    // Fallback: Basic HTML entity encoding
    return input
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;")
        .replace(/`/g, "&#x60;")
        .replace(/\//g, "&#x2F;");
};

/**
 * Sanitizes a URL to prevent javascript: and data: protocol attacks
 * @param {string} url - URL to sanitize
 * @returns {string|null} - Sanitized URL or null if invalid
 */
export const sanitizeUrl = (url) => {
    if (!url) return null;

    const trimmedUrl = url.trim().toLowerCase();

    // Block dangerous protocols (need to reference them to block them)
    // eslint-disable-next-line no-script-url
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
    for (const protocol of dangerousProtocols) {
        if (trimmedUrl.startsWith(protocol)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`[SECURITY] Blocked dangerous URL: ${url}`);
            }
            return null;
        }
    }

    // Ensure it starts with allowed protocols or is a relative URL
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
    const isRelative = url.startsWith('/') || url.startsWith('#') || url.startsWith('.');
    const hasAllowedProtocol = allowedProtocols.some(p => trimmedUrl.startsWith(p));

    if (!isRelative && !hasAllowedProtocol) {
        // Add https:// if no protocol specified and looks like a domain
        // Domain pattern: allows alphanumeric, hyphens, and dots
        /* eslint-disable-next-line no-useless-escape */
        const domainRegex = /^[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,}(\/.*)?$/i;
        if (domainRegex.test(url)) {
            return `https://${url}`;
        }
        return null;
    }

    return url;
};

/**
 * Rate limiter for form submissions
 * @param {string} key - Unique key for the action
 * @param {number} limitMs - Time limit in milliseconds
 * @returns {boolean} - True if action is allowed
 */
const rateLimitStore = new Map();

export const checkRateLimit = (key, limitMs = 1000) => {
    const now = Date.now();
    const lastAction = rateLimitStore.get(key);

    if (lastAction && now - lastAction < limitMs) {
        return false; // Rate limited
    }

    rateLimitStore.set(key, now);
    return true; // Action allowed
};

/**
 * Validates file upload (for future use)
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export const validateFileUpload = (file, options = {}) => {
    const {
        maxSizeBytes = 5 * 1024 * 1024, // 5MB default
        allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
        allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf']
    } = options;

    const errors = [];

    if (!file) {
        errors.push('No file provided');
        return { isValid: false, errors };
    }

    // Check file size
    if (file.size > maxSizeBytes) {
        errors.push(`File size exceeds ${maxSizeBytes / (1024 * 1024)}MB limit`);
    }

    // Check MIME type
    if (!allowedTypes.includes(file.type)) {
        errors.push(`File type ${file.type} is not allowed`);
    }

    // Check file extension
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
        errors.push(`File extension ${extension} is not allowed`);
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Content Security Policy nonce generator
 * @returns {string} - Random nonce string
 */
export const generateNonce = () => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Validates and sanitizes form data object
 * @param {Object} formData - Form data to sanitize
 * @returns {Object} - Sanitized form data
 */
export const sanitizeFormData = (formData) => {
    const sanitized = {};

    for (const [key, value] of Object.entries(formData)) {
        if (typeof value === 'string') {
            sanitized[key] = sanitizeInput(value.trim());
        } else {
            sanitized[key] = value;
        }
    }

    return sanitized;
};
